import { Button } from "@/components/flexkit/Button";
import { useGetWorkflow, useUpdateWorkflow } from "@/queries/automation-queries";
import { editableStepsAtom } from "./state";
import { useStore } from "jotai";
import { convertFormStepsToWorkflowStep } from "@/components/automations/automation-edit-form-data";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { FC } from "react";
import { useSetAtom } from "jotai";
import { isEditingStepsAtom } from "./state";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
/**
 * This button saves the changes made to automation steps.
 * Changes are stored in {@link editableStepsAtom}. This button takes the changes and saves them to the current automation in automation details page..
 */
export const SaveAutomationStepsButton: FC<{ workflowId: string }> = ({ workflowId }) => {
    const { mutateAsync: updateWorkflow, isPending: isUpdatingWorkflow } = useUpdateWorkflow();
    const { data: workflow } = useGetWorkflow(workflowId);
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const store = useStore();
    const { toast } = useToast();

    const save = async () => {
        const steps = store.get(editableStepsAtom);
        if (!steps || !workflow) {
            return;
        }

        try {
            const currentLimits = workflow.spec?.action?.limits;
            await updateWorkflow({
                workflowId,
                action: new WorkflowAction({
                    limits: new WorkflowAction_Limits({
                        maxParallel: currentLimits?.maxParallel || DEFAULT_WORKFLOW_LIMITS.maxParallel,
                        maxTotal: currentLimits?.maxTotal || DEFAULT_WORKFLOW_LIMITS.maxTotal,
                    }),
                    steps: convertFormStepsToWorkflowStep(steps),
                }),
            });
            setIsEditingSteps(false);
        } catch (error) {
            toast({
                title: "Failed to save changes to the automation",
                description: formatError(error),
            });
        }
    };

    return (
        <Button
            variant="primary"
            data-tracking-id="save-automation-steps-automation-details-page"
            loading={isUpdatingWorkflow}
            onClick={save}
        >
            Save
        </Button>
    );
};
